import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const LeadTimeLogisticsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Avg Lead Time',
    value: '18.5 days',
    change: '-2.3 days',
    isPositive: true,
    subtitle: 'End-to-end lead time'
  }, {
    title: 'Delayed Shipments %',
    value: '8.2%',
    change: '-1.5%',
    isPositive: true,
    subtitle: 'Late deliveries'
  }, {
    title: 'Transit Damage %',
    value: '0.4%',
    change: '-0.1%',
    isPositive: true,
    subtitle: 'Damaged in transit'
  }];
  // Lead Time Trend Data
  const leadTimeTrendData = [{
    month: 'Jan',
    avgLeadTime: 22.5,
    domesticLeadTime: 15.2,
    importLeadTime: 35.8,
    target: 20
  }, {
    month: 'Feb',
    avgLeadTime: 21.8,
    domesticLeadTime: 14.8,
    importLeadTime: 34.5,
    target: 20
  }, {
    month: 'Mar',
    avgLeadTime: 21.2,
    domesticLeadTime: 14.5,
    importLeadTime: 33.8,
    target: 20
  }, {
    month: 'Apr',
    avgLeadTime: 20.5,
    domesticLeadTime: 14.2,
    importLeadTime: 32.5,
    target: 20
  }, {
    month: 'May',
    avgLeadTime: 19.8,
    domesticLeadTime: 13.8,
    importLeadTime: 31.2,
    target: 20
  }, {
    month: 'Jun',
    avgLeadTime: 19.2,
    domesticLeadTime: 13.5,
    importLeadTime: 30.5,
    target: 20
  }, {
    month: 'Jul',
    avgLeadTime: 18.8,
    domesticLeadTime: 13.2,
    importLeadTime: 29.8,
    target: 20
  }, {
    month: 'Aug',
    avgLeadTime: 18.5,
    domesticLeadTime: 13.0,
    importLeadTime: 29.2,
    target: 20
  }, {
    month: 'Sep',
    avgLeadTime: 18.5,
    domesticLeadTime: 12.8,
    importLeadTime: 29.5,
    target: 20
  }];
  // Lane-wise Transit Time Data
  const laneTransitTimeData = [{
    lane: 'Mumbai-Delhi',
    transitTime: 3.2,
    delayRate: 6.5,
    volume: 2850
  }, {
    lane: 'Chennai-Bangalore',
    transitTime: 1.8,
    delayRate: 4.2,
    volume: 3200
  }, {
    lane: 'Pune-Mumbai',
    transitTime: 1.2,
    delayRate: 3.8,
    volume: 4100
  }, {
    lane: 'Delhi-Chandigarh',
    transitTime: 2.5,
    delayRate: 5.2,
    volume: 2400
  }, {
    lane: 'Kolkata-Bhubaneswar',
    transitTime: 2.8,
    delayRate: 7.8,
    volume: 1850
  }, {
    lane: 'Ahmedabad-Vadodara',
    transitTime: 1.5,
    delayRate: 4.5,
    volume: 2650
  }, {
    lane: 'Hyderabad-Vijayawada',
    transitTime: 2.2,
    delayRate: 6.2,
    volume: 2100
  }, {
    lane: 'Bangalore-Chennai',
    transitTime: 1.9,
    delayRate: 5.8,
    volume: 2950
  }];
  // Logistics Delay Heatmap Data
  const delayHeatmapData = [{
    zone: 'North',
    week1: 5.2,
    week2: 6.8,
    week3: 7.5,
    week4: 8.2
  }, {
    zone: 'West',
    week1: 4.5,
    week2: 5.2,
    week3: 6.8,
    week4: 7.2
  }, {
    zone: 'South',
    week1: 3.8,
    week2: 4.5,
    week3: 5.2,
    week4: 6.5
  }, {
    zone: 'East',
    week1: 8.5,
    week2: 9.2,
    week3: 10.5,
    week4: 11.2
  }];
  // Route Performance Table Data
  const routePerformanceData = [{
    route: 'Mumbai-Delhi',
    origin: 'Mumbai',
    destination: 'Delhi',
    distance: 1450,
    avgTransitTime: 3.2,
    targetTransitTime: 3.0,
    onTimeDelivery: 93.5,
    delayRate: 6.5,
    avgDelay: 0.8,
    transitDamage: 0.3,
    volume: 2850,
    cost: 12500,
    carrier: 'Blue Dart',
    mode: 'Road',
    performance: 'Good'
  }, {
    route: 'Chennai-Bangalore',
    origin: 'Chennai',
    destination: 'Bangalore',
    distance: 350,
    avgTransitTime: 1.8,
    targetTransitTime: 1.5,
    onTimeDelivery: 95.8,
    delayRate: 4.2,
    avgDelay: 0.5,
    transitDamage: 0.2,
    volume: 3200,
    cost: 4200,
    carrier: 'DTDC',
    mode: 'Road',
    performance: 'Excellent'
  }, {
    route: 'Pune-Mumbai',
    origin: 'Pune',
    destination: 'Mumbai',
    distance: 150,
    avgTransitTime: 1.2,
    targetTransitTime: 1.0,
    onTimeDelivery: 96.2,
    delayRate: 3.8,
    avgDelay: 0.3,
    transitDamage: 0.1,
    volume: 4100,
    cost: 2800,
    carrier: 'Delhivery',
    mode: 'Road',
    performance: 'Excellent'
  }, {
    route: 'Delhi-Chandigarh',
    origin: 'Delhi',
    destination: 'Chandigarh',
    distance: 250,
    avgTransitTime: 2.5,
    targetTransitTime: 2.0,
    onTimeDelivery: 94.8,
    delayRate: 5.2,
    avgDelay: 0.6,
    transitDamage: 0.3,
    volume: 2400,
    cost: 3500,
    carrier: 'Blue Dart',
    mode: 'Road',
    performance: 'Good'
  }, {
    route: 'Kolkata-Bhubaneswar',
    origin: 'Kolkata',
    destination: 'Bhubaneswar',
    distance: 450,
    avgTransitTime: 2.8,
    targetTransitTime: 2.5,
    onTimeDelivery: 92.2,
    delayRate: 7.8,
    avgDelay: 1.2,
    transitDamage: 0.5,
    volume: 1850,
    cost: 5200,
    carrier: 'Gati',
    mode: 'Road',
    performance: 'Fair'
  }, {
    route: 'Ahmedabad-Vadodara',
    origin: 'Ahmedabad',
    destination: 'Vadodara',
    distance: 110,
    avgTransitTime: 1.5,
    targetTransitTime: 1.2,
    onTimeDelivery: 95.5,
    delayRate: 4.5,
    avgDelay: 0.4,
    transitDamage: 0.2,
    volume: 2650,
    cost: 2200,
    carrier: 'Delhivery',
    mode: 'Road',
    performance: 'Good'
  }, {
    route: 'Hyderabad-Vijayawada',
    origin: 'Hyderabad',
    destination: 'Vijayawada',
    distance: 275,
    avgTransitTime: 2.2,
    targetTransitTime: 2.0,
    onTimeDelivery: 93.8,
    delayRate: 6.2,
    avgDelay: 0.7,
    transitDamage: 0.4,
    volume: 2100,
    cost: 3800,
    carrier: 'DTDC',
    mode: 'Road',
    performance: 'Good'
  }, {
    route: 'Bangalore-Chennai',
    origin: 'Bangalore',
    destination: 'Chennai',
    distance: 350,
    avgTransitTime: 1.9,
    targetTransitTime: 1.5,
    onTimeDelivery: 94.2,
    delayRate: 5.8,
    avgDelay: 0.6,
    transitDamage: 0.3,
    volume: 2950,
    cost: 4500,
    carrier: 'Blue Dart',
    mode: 'Road',
    performance: 'Good'
  }, {
    route: 'Mumbai-Pune',
    origin: 'Mumbai',
    destination: 'Pune',
    distance: 150,
    avgTransitTime: 1.3,
    targetTransitTime: 1.0,
    onTimeDelivery: 95.8,
    delayRate: 4.2,
    avgDelay: 0.4,
    transitDamage: 0.2,
    volume: 3850,
    cost: 2900,
    carrier: 'Delhivery',
    mode: 'Road',
    performance: 'Excellent'
  }, {
    route: 'Delhi-Jaipur',
    origin: 'Delhi',
    destination: 'Jaipur',
    distance: 280,
    avgTransitTime: 2.8,
    targetTransitTime: 2.5,
    onTimeDelivery: 91.5,
    delayRate: 8.5,
    avgDelay: 1.0,
    transitDamage: 0.6,
    volume: 1950,
    cost: 4200,
    carrier: 'Gati',
    mode: 'Road',
    performance: 'Fair'
  }];
  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'Excellent':
        return 'bg-green-100 text-green-700';
      case 'Good':
        return 'bg-blue-100 text-blue-700';
      case 'Fair':
        return 'bg-yellow-100 text-yellow-700';
      case 'Poor':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getDelayColor = (delay: number) => {
    if (delay < 5) return '#10b981';
    if (delay < 7) return '#3b82f6';
    if (delay < 9) return '#f59e0b';
    return '#ef4444';
  };
  return <div data-id="element-9414">
      <DescriptionPanel title="Lead Time & Logistics Performance" description="Monitor end-to-end logistics performance across routes, carriers, and zones. Track lead times, transit delays, and damage rates to optimize supply chain efficiency and reduce logistics costs." insights={['Average lead time reduced to 18.5 days - 2.3 days improvement through route optimization', 'Delayed shipments at 8.2% - down 1.5% from last quarter with better carrier management', 'Transit damage rate at 0.4% - improved packaging and handling protocols showing results', 'East zone showing highest delays at 11.2% - requires immediate attention and corrective action']} data-id="element-9415" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-9416">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-9417">
          Logistics Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9418">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-9419">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-9420">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-9421">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-9422">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-9423">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-9424" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-9425" />}
                {kpi.change} vs last quarter
              </div>
            </Card>)}
        </div>
      </div>
      {/* Lead Time Trend and Lane-wise Transit Time */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-9426">
        {/* Lead Time Trend */}
        <Card className="p-6" data-id="element-9427">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9428">
            Lead Time Trend Analysis
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9429">
            Monthly lead time performance (days)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9430">
            <LineChart data={leadTimeTrendData} data-id="element-9431">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9432" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-9433" />
              <YAxis tick={{
              fontSize: 12
            }} domain={[0, 40]} data-id="element-9434" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `${value.toFixed(1)} days`} data-id="element-9435" />
              <Legend data-id="element-9436" />
              <Line type="monotone" dataKey="avgLeadTime" stroke="#3b82f6" strokeWidth={3} name="Average Lead Time" dot={{
              r: 5
            }} data-id="element-9437" />
              <Line type="monotone" dataKey="domesticLeadTime" stroke="#10b981" strokeWidth={2} name="Domestic" dot={{
              r: 4
            }} data-id="element-9438" />
              <Line type="monotone" dataKey="importLeadTime" stroke="#ef4444" strokeWidth={2} name="Import" dot={{
              r: 4
            }} data-id="element-9439" />
              <Line type="monotone" dataKey="target" stroke="#9ca3af" strokeWidth={2} strokeDasharray="5 5" name="Target" dot={false} data-id="element-9440" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9441">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-9442">
              <div className="text-center" data-id="element-9443">
                <div className="text-gray-600" data-id="element-9444">Current Avg</div>
                <div className="text-xl font-bold text-blue-600" data-id="element-9445">18.5d</div>
              </div>
              <div className="text-center" data-id="element-9446">
                <div className="text-gray-600" data-id="element-9447">Domestic</div>
                <div className="text-xl font-bold text-green-600" data-id="element-9448">12.8d</div>
              </div>
              <div className="text-center" data-id="element-9449">
                <div className="text-gray-600" data-id="element-9450">Import</div>
                <div className="text-xl font-bold text-red-600" data-id="element-9451">29.5d</div>
              </div>
              <div className="text-center" data-id="element-9452">
                <div className="text-gray-600" data-id="element-9453">Target</div>
                <div className="text-xl font-bold text-gray-600" data-id="element-9454">20.0d</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Lane-wise Transit Time Chart */}
        <Card className="p-6" data-id="element-9455">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9456">
            Lane-wise Transit Time Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-9457">
            Transit time and delay rate by major lanes (days)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-9458">
            <ComposedChart data={laneTransitTimeData} layout="vertical" data-id="element-9459">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-9460" />
              <XAxis type="number" tick={{
              fontSize: 12
            }} data-id="element-9461" />
              <YAxis dataKey="lane" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-9462" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: any, name: string) => {
              if (name === 'Transit Time') return `${value} days`;
              if (name === 'Delay Rate') return `${value}%`;
              return value;
            }} data-id="element-9463" />
              <Legend data-id="element-9464" />
              <Bar dataKey="transitTime" fill="#3b82f6" name="Transit Time" barSize={20} data-id="element-9465" />
              <Line type="monotone" dataKey="delayRate" stroke="#ef4444" strokeWidth={2} name="Delay Rate" dot={{
              r: 4
            }} data-id="element-9466" />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-9467">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-9468">
              <div className="text-center" data-id="element-9469">
                <div className="text-gray-600" data-id="element-9470">Fastest Lane</div>
                <div className="text-lg font-bold text-green-600" data-id="element-9471">
                  Pune-Mumbai
                </div>
                <div className="text-xs text-gray-500" data-id="element-9472">1.2 days</div>
              </div>
              <div className="text-center" data-id="element-9473">
                <div className="text-gray-600" data-id="element-9474">Slowest Lane</div>
                <div className="text-lg font-bold text-red-600" data-id="element-9475">
                  Mumbai-Delhi
                </div>
                <div className="text-xs text-gray-500" data-id="element-9476">3.2 days</div>
              </div>
              <div className="text-center" data-id="element-9477">
                <div className="text-gray-600" data-id="element-9478">Highest Delay</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-9479">
                  Kolkata-Bhub.
                </div>
                <div className="text-xs text-gray-500" data-id="element-9480">7.8%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Logistics Delay Heatmap */}
      <Card className="p-6 mb-8" data-id="element-9481">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-9482">
          Logistics Delay Heatmap by Zone
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-9483">
          Weekly delay rate by zone (%)
        </p>
        <div className="overflow-x-auto" data-id="element-9484">
          <table className="w-full" data-id="element-9485">
            <thead data-id="element-9486">
              <tr className="border-b-2 border-gray-300" data-id="element-9487">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9488">
                  Zone
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9489">
                  Week 1
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9490">
                  Week 2
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9491">
                  Week 3
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9492">
                  Week 4
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9493">
                  Avg
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9494">
              {delayHeatmapData.map((row, index) => {
              const avg = ((row.week1 + row.week2 + row.week3 + row.week4) / 4).toFixed(1);
              return <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9495">
                    <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-9496">
                      {row.zone}
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-9497">
                      <div className="inline-flex items-center justify-center w-16 h-10 rounded-lg text-sm font-bold text-white" style={{
                    backgroundColor: getDelayColor(row.week1)
                  }} data-id="element-9498">
                        {row.week1}%
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-9499">
                      <div className="inline-flex items-center justify-center w-16 h-10 rounded-lg text-sm font-bold text-white" style={{
                    backgroundColor: getDelayColor(row.week2)
                  }} data-id="element-9500">
                        {row.week2}%
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-9501">
                      <div className="inline-flex items-center justify-center w-16 h-10 rounded-lg text-sm font-bold text-white" style={{
                    backgroundColor: getDelayColor(row.week3)
                  }} data-id="element-9502">
                        {row.week3}%
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-9503">
                      <div className="inline-flex items-center justify-center w-16 h-10 rounded-lg text-sm font-bold text-white" style={{
                    backgroundColor: getDelayColor(row.week4)
                  }} data-id="element-9504">
                        {row.week4}%
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-9505">
                      <span className="text-sm font-bold text-gray-900" data-id="element-9506">
                        {avg}%
                      </span>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9507">
          <div className="flex items-center justify-between" data-id="element-9508">
            <div className="flex items-center space-x-6" data-id="element-9509">
              <div className="flex items-center" data-id="element-9510">
                <div className="w-6 h-6 rounded bg-green-500 mr-2" data-id="element-9511" />
                <span className="text-sm text-gray-600" data-id="element-9512">Low (&lt;5%)</span>
              </div>
              <div className="flex items-center" data-id="element-9513">
                <div className="w-6 h-6 rounded bg-blue-500 mr-2" data-id="element-9514" />
                <span className="text-sm text-gray-600" data-id="element-9515">Medium (5-7%)</span>
              </div>
              <div className="flex items-center" data-id="element-9516">
                <div className="w-6 h-6 rounded bg-orange-500 mr-2" data-id="element-9517" />
                <span className="text-sm text-gray-600" data-id="element-9518">High (7-9%)</span>
              </div>
              <div className="flex items-center" data-id="element-9519">
                <div className="w-6 h-6 rounded bg-red-500 mr-2" data-id="element-9520" />
                <span className="text-sm text-gray-600" data-id="element-9521">Critical (&gt;9%)</span>
              </div>
            </div>
            <div className="text-sm text-gray-600" data-id="element-9522">
              Overall Average Delay:{' '}
              <span className="font-bold text-gray-900" data-id="element-9523">6.8%</span>
            </div>
          </div>
        </div>
      </Card>
      {/* Route Performance Table */}
      <Card className="p-6" data-id="element-9524">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-9525">
          Route Performance Analysis - Top 10 Routes
        </h3>
        <div className="overflow-x-auto" data-id="element-9526">
          <table className="w-full" data-id="element-9527">
            <thead data-id="element-9528">
              <tr className="border-b-2 border-gray-300" data-id="element-9529">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9530">
                  Route
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9531">
                  Origin
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9532">
                  Destination
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9533">
                  Distance (km)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9534">
                  Avg Transit
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9535">
                  Target
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9536">
                  OTD %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9537">
                  Delay %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9538">
                  Avg Delay
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9539">
                  Damage %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9540">
                  Volume
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9541">
                  Cost/Unit
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9542">
                  Carrier
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9543">
                  Mode
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-9544">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody data-id="element-9545">
              {routePerformanceData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-9546">
                  <td className="py-3 px-4 text-sm font-semibold text-gray-800" data-id="element-9547">
                    {row.route}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9548">
                    {row.origin}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9549">
                    {row.destination}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9550">
                    {row.distance}
                  </td>
                  <td className="py-3 px-4 text-center text-sm font-medium text-blue-600" data-id="element-9551">
                    {row.avgTransitTime}d
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-600" data-id="element-9552">
                    {row.targetTransitTime}d
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9553">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.onTimeDelivery >= 95 ? 'bg-green-100 text-green-700' : row.onTimeDelivery >= 90 ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-9554">
                      {row.onTimeDelivery}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9555">
                    <span className={`text-sm font-bold ${row.delayRate < 5 ? 'text-green-600' : row.delayRate < 7 ? 'text-blue-600' : 'text-red-600'}`} data-id="element-9556">
                      {row.delayRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9557">
                    {row.avgDelay}d
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9558">
                    <span className={`text-sm font-bold ${row.transitDamage < 0.3 ? 'text-green-600' : row.transitDamage < 0.5 ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-9559">
                      {row.transitDamage}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9560">
                    {row.volume}
                  </td>
                  <td className="py-3 px-4 text-right text-sm text-gray-700" data-id="element-9561">
                    ₹{row.cost}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9562">
                    {row.carrier}
                  </td>
                  <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9563">
                    {row.mode}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-9564">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${getPerformanceColor(row.performance)}`} data-id="element-9565">
                      {row.performance}
                    </span>
                  </td>
                </tr>)}
              {/* Average Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-9566">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-9567">AVERAGE</td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9568">-</td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9569">-</td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9570">
                  382
                </td>
                <td className="py-3 px-4 text-center text-sm text-blue-600" data-id="element-9571">
                  2.1d
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-600" data-id="element-9572">
                  1.8d
                </td>
                <td className="py-3 px-4 text-center" data-id="element-9573">
                  <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700" data-id="element-9574">
                    94.3%
                  </span>
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9575">
                  5.7%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9576">
                  0.6d
                </td>
                <td className="py-3 px-4 text-center text-sm text-green-600" data-id="element-9577">
                  0.3%
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-900" data-id="element-9578">
                  2,690
                </td>
                <td className="py-3 px-4 text-right text-sm text-gray-900" data-id="element-9579">
                  ₹4,580
                </td>
                <td className="py-3 px-4 text-sm text-gray-700" data-id="element-9580">-</td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9581">
                  -
                </td>
                <td className="py-3 px-4 text-center text-sm text-gray-700" data-id="element-9582">
                  -
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-9583">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-9584">
            <div className="text-center" data-id="element-9585">
              <div className="text-gray-600" data-id="element-9586">Total Routes</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9587">10</div>
            </div>
            <div className="text-center" data-id="element-9588">
              <div className="text-gray-600" data-id="element-9589">Avg Transit Time</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-9590">2.1 days</div>
            </div>
            <div className="text-center" data-id="element-9591">
              <div className="text-gray-600" data-id="element-9592">Avg OTD</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9593">94.3%</div>
            </div>
            <div className="text-center" data-id="element-9594">
              <div className="text-gray-600" data-id="element-9595">Avg Delay Rate</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9596">5.7%</div>
            </div>
            <div className="text-center" data-id="element-9597">
              <div className="text-gray-600" data-id="element-9598">Avg Damage</div>
              <div className="text-xl font-bold text-green-600" data-id="element-9599">0.3%</div>
            </div>
            <div className="text-center" data-id="element-9600">
              <div className="text-gray-600" data-id="element-9601">Total Volume</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-9602">26,900</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default LeadTimeLogisticsDashboard;